import { Content, ProductError, ProductErrorType } from '../../models';
import { GameLevel } from '../game-config';

export class VraiFauxAnswer {
  label: string;
  isCorrect: boolean;
  isSelected?: boolean;

  constructor(rawData: VraiFauxAnswer) {
    this.label = rawData.label;
    this.isCorrect = rawData.isCorrect;
    this.isSelected = rawData.isSelected || false; // initialize it to false (undefined => false)
  }
}

export class VraiFauxQuestion {
  readonly index: number;
  contents: Content[];
  answers: VraiFauxAnswer[];
  explanations: Content[];
  difficulty?: GameLevel;

  constructor(rawData?: VraiFauxQuestion, index?: number) {
    if (rawData) {
      this.index = index || index === 0 ? index : rawData.index;
      this.contents = (rawData.contents || []).map(c => new Content(c));
      this.explanations = (rawData.explanations || []).map(c => new Content(c));
      this.answers = rawData.answers.map(a => new VraiFauxAnswer(a));
      this.difficulty = rawData.difficulty;
    }
  }

  static validate(question: VraiFauxQuestion, questionIndex: number): ProductError[] {
    const questionErrors: ProductError[] = [];
    if (!question.contents || !question.contents.length) {
      questionErrors.push({ type: ProductErrorType.MISSING, property: 'contents', payload: { questionIndex } });
    }
    question.contents
      .map((c, i) => Content.validate(c, i))
      .reduce((flattened, subError) => flattened.concat(subError), [])
      .forEach(error => {
        error.payload.tileIndex = questionIndex;
        error.payload.questionField = 'contents';
        questionErrors.push(error);
      });
    if (!question.answers || !question.answers.length) {
      questionErrors.push({ type: ProductErrorType.MISSING, property: 'answers', payload: { questionIndex } });
    }
    return questionErrors;
  }
}

export class VraiFauxData {
  configuration: {
    labelChange: number;
    labelLeft: string;
    labelRight: string;
  };
  questions: VraiFauxQuestion[];

  constructor(rawData?: VraiFauxData) {
    const questions = (rawData && rawData.questions) || [];
    this.questions = questions.map(q => new VraiFauxQuestion(q));
    this.configuration = rawData?.configuration
      ? rawData.configuration
      : {
          labelChange: 0,
          labelLeft: '',
          labelRight: '',
        };
  }

  static validate(data: VraiFauxData): ProductError[] {
    if (!data.questions || !data.questions.length) {
      return [{ type: ProductErrorType.MISSING, property: 'questions' }];
    }
    if (data?.configuration?.labelChange && !data.configuration.labelLeft.length) {
      return [{ type: ProductErrorType.CANNOT_BE_NULL, property: 'vrai-faux-label-left' }];
    }
    if (data?.configuration?.labelChange && !data.configuration.labelRight.length) {
      return [{ type: ProductErrorType.CANNOT_BE_NULL, property: 'vrai-faux-label-right' }];
    }
    if (data?.configuration?.labelChange && data.configuration.labelLeft.length > 12) {
      return [{ type: ProductErrorType.TOO_MUCH_VALUES, property: 'vrai-faux-label-left-too-much' }];
    }
    if (data?.configuration?.labelChange && data.configuration.labelRight.length > 12) {
      return [{ type: ProductErrorType.TOO_MUCH_VALUES, property: 'vrai-faux-label-right-too-much' }];
    }
    return data.questions
      .map((question, questionIndex) => VraiFauxQuestion.validate(question, questionIndex))
      .reduce((agr, arr) => agr.concat(arr), []);
  }
}
